import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import { toast } from "react-toastify";
import { useLocation, useParams } from "react-router-dom";

import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import RatingsContentSwitch from "./diff/RatingsContentSwitch";
import SleRatingTable from "../display/SleRatingTable";
import useCommentSave from "../../hooks/ratings/useCommentSave";
import useCommentState from "../../hooks/ratings/useCommentState";
import useGroupContext from "../../hooks/ratings/useGroupContext";
import useGroupRatingsLoader from "../../hooks/ratings/useGroupRatingsLoader";
import useGroupSelection from "../../hooks/ratings/useGroupSelection";
import useGroupSleRatings from "../../hooks/useGroupSleRatings";
import useRatingSave from "../../hooks/ratings/useRatingSave";
import useRatingState from "../../hooks/ratings/useRatingState";
import useSubjectSelection from "../../hooks/useSubjectSelection";
import useAutoSave from "../../hooks/useAutoSave";
import useFlushOnLeave from "../../hooks/ratings/useFlushOnLeave";

import {
    buildSleGroupInfoDto,
    flattenGroupSleStudents,
    resolveSchoolLevelTemplateOverrideForSle
} from "../../utils/teacher/SleBatchUtils";
import { buildContentItems } from "../../utils/teacher/StudentPageUtils";
import { extractStudentGradingProfileId, getGradingCriteriaId } from "../../utils/teacher/InputRatingUtils";
import { removeHiddenChars } from "../../utils/removeHiddenChars";
import { GlobalContext } from "../contextProvider/ContextProvider";
import { useEnsureSchoolTemplatesForLocation } from "../contextProvider/ContextSetter";

import "../../styles/BatchStudentRatings.scss";

/**
 * GroupRatings — batch rating view for GROUPS mode.
 *
 * Responsibilities:
 *   - Selection sequence: school → grade → subject → groups
 *   - Routing effects: auto-set school location, auto-select single grade,
 *     ensure school template for SLE
 *   - Restore preselected groups from router state (useGroupSelection)
 *   - Fetch/init lifecycle via useGroupRatingsLoader
 *   - Save path: useRatingSave / useCommentSave / useAutoSave
 *   - SLE: useGroupSleRatings
 *   - Rendering: dropdowns, RatingGrid, SleRatingTable
 *
 * @param {Object}   props.state          - local state from BatchStudentRatings
 * @param {Function} props.dispatch
 * @param {string}   [props.basePath]
 * @param {string}   [props.teacherGuid]  - override guid for admin-viewing-teacher
 */
const GroupRatings = ({ dispatch, state, basePath, teacherGuid: overrideTeacherGuid }) => {
    const { state: locationState, pathname } = useLocation();
    const { locationKey: urlLocationKey } = useParams();
    const ensureTemplatesForLocation = useEnsureSchoolTemplatesForLocation();

    const { state: globalState } = useContext(GlobalContext);
    const {
        actionWindowsDto,
        allLocationDtos,
        currentTimePeriodDto,
        schoolLevelTemplateDto,
        schoolTemplatesByLocation,
        schoolYearDto,
        token,
        userDetails,
        viewTimePeriodDto
    } = globalState || {};

    const { gradingProfiles, navCollapsed, selectedEpr } = state;

    // ── Local state (selection, UI) ────────────────────────────────────────────
    const [allSharedGroupOptions, setAllSharedGroupOptions] = useState([]);
    const [sharedGradingProfileDtos, setSharedGradingProfileDtos] = useState([]);
    const [selectedGrade, setSelectedGrade] = useState(locationState?.selectedGrade || null);
    const [selectedSchoolLocationKey, setSelectedSchoolLocationKey] = useState(null);
    const [sleSelected, setSleSelected] = useState(Boolean(locationState?.sleSelected));

    // ── Rating + comment state ─────────────────────────────────────────────────
    const {
        ratings,
        ratingsLoaded,
        hasChanges: hasRatingChanges,
        setHasChanges: setHasRatingChanges,
        initializeFromServer: initRatings,
        setRating,
        setBulkRating,
        resetChanges: resetRatings,
        setRatingsLoaded
    } = useRatingState();

    const {
        comments,
        fluencyByKey,
        existingComments,
        hasCommentChanges,
        initializeFromServer: initComments,
        syncFromServer: syncComments,
        setComment,
        setFluencyWpm,
        resetComments,
        setHasCommentChanges
    } = useCommentState({ mode: "GROUPS" });

    const hasChanges = hasRatingChanges || hasCommentChanges;

    // ── EPR / profiles ─────────────────────────────────────────────────────────
    const effectiveTimePeriodDto =
        (viewTimePeriodDto || currentTimePeriodDto) ??
        (gradingProfiles?.find((p) => p.gradeName === selectedGrade) || null);

    const gradingProfilesCurrentSem = useMemo(
        () => (gradingProfiles || []).filter((p) => Number(p.timePeriod) === Number(effectiveTimePeriodDto?.key)),
        [gradingProfiles, effectiveTimePeriodDto?.key]
    );

    // ── Shared profile IDs (drives DTO fetch inside useGroupRatingsLoader) ──────
    const sharedGradingProfileIds = useMemo(() => {
        const ids = new Set();
        allSharedGroupOptions.forEach((g) => {
            if (g.sharedGradingProfile) ids.add(Number(g.sharedGradingProfile));
        });
        return [...ids];
    }, [allSharedGroupOptions]);

    // ── School template override for SLE ──────────────────────────────────────
    const schoolLevelTemplateOverrideForSle = useMemo(
        () =>
            resolveSchoolLevelTemplateOverrideForSle({
                view: "GROUPS",
                selectedSchoolLocationKey,
                urlLocationKey,
                schoolTemplatesByLocation
            }),
        [selectedSchoolLocationKey, urlLocationKey, schoolTemplatesByLocation]
    );

    const schoolProfileLocationKey =
        schoolLevelTemplateOverrideForSle?.firstSemester?.profileLocationKey ??
        schoolLevelTemplateDto?.firstSemester?.profileLocationKey ??
        "";

    // ── selectedTemplate owned here — passed to both useGroupSelection and
    //    useSubjectSelection to avoid circular dependency ──────────────────────
    // Stable no-op: must NOT be an inline () => {} (causes infinite loops).
    const noop = useCallback(() => {}, []);
    const [selectedTemplate, setSelectedTemplate] = useState(null);

    const {
        combinedGrades,
        gradesForGroupSelect,
        groupOptions,
        handleGroupGradeChange,
        handleSubjectSelect,
        hasSleSubjectOption,
        resetForGroupGradeChange,
        selectedGroups,
        setSelectedGroups,
        shouldShowGroupEprSelect,
        showSchoolSelect,
        sharedLocationKeys
    } = useGroupSelection({
        allSharedGroupOptions,
        gradingProfilesCurrentSem,
        locationState,
        pathname,
        selectedEpr,
        selectedGrade,
        setSelectedGrade,
        selectedSchoolLocationKey,
        selectedTemplate,
        setSelectedTemplate,
        sleSelected,
        setSleSelected,
        sharedGradingProfileDtos,
        setSubjectOptions: noop,
        dispatch,
        ratingState: {
            resetRatings,
            resetComments,
            setExistingRatingsData: () => {}, // managed by existingRatingsDataForSave memo
            setExistingStudentComments: () => {} // managed by loader
        }
    });

    const { subjectOptions } = useSubjectSelection({
        combinedGrades,
        selectedEpr,
        schoolLevelTemplateDto,
        schoolLevelTemplateOverride: schoolLevelTemplateOverrideForSle,
        incomingTemplateId: locationState?.subjectId ?? null,
        currentTimePeriodName: currentTimePeriodDto?.name,
        selectedGrade,
        view: "GROUPS",
        allSharedGroupOptions,
        gradingProfiles,
        sharedGradingProfileDtos,
        selectedTemplate,
        setSelectedTemplate,
        selectedSchoolLocationKey
    });

    // ── Fetch / init lifecycle ─────────────────────────────────────────────────
    // Called after useGroupSelection so selectedGroups is available.
    // sharedGradingProfileDtos lives in component state (above) and is passed
    // to both the loader (for enrichment) and the selection hooks (for rebuild).
    const { fetchSelectedGroupInfo, fetchSelectedGroupStudentComments, selectedGroupContent } = useGroupRatingsLoader({
        selectedGroups,
        selectedGrade,
        selectedTemplate,
        selectedEpr,
        gradingProfilesCurrentSem,
        gradingProfiles,
        sharedGradingProfileIds,
        locationState,
        userDetails,
        schoolYearDto,
        currentTimePeriodDto,
        token,
        overrideTeacherGuid,
        setAllSharedGroupOptions,
        setSharedGradingProfileDtos,
        initRatings,
        initComments,
        dispatch
    });

    // ── GroupContext ───────────────────────────────────────────────────────────
    const effectiveEprForContext =
        selectedEpr ??
        (selectedGrade ? (gradingProfilesCurrentSem?.find((p) => p.gradeName === selectedGrade) ?? null) : null);

    const { groupContext } = useGroupContext({
        selectedGroupContent,
        effectiveEpr: effectiveEprForContext,
        selectedTemplate
    });

    // ── Content items / save hooks ────────────────────────────────────────────
    const contentItemsForSave = useMemo(
        () => (selectedTemplate ? buildContentItems(selectedTemplate) : []),
        [selectedTemplate]
    );

    // For GROUPS mode, existingRatingsData for the save path is derived from
    // selectedGroupContent (the enriched server response). This provides the
    // existingKey and gradingCriteria per record that buildRatingMap needs to
    // correctly include cleared ratings (existingKey !== null) in the payload.
    // Without this, clearing a rating produces existingKey: null → skipped by
    // buildRatingPayload → old rating persists on the server.
    const existingRatingsDataForSave = useMemo(
        () =>
            selectedGroupContent.flatMap((group) =>
                (group.groupStudentContents || []).map((s) => ({
                    key: `${group.groupId}-${s.gradingProfileStudentId}`,
                    contentRatings: s.contentRatings || []
                }))
            ),
        [selectedGroupContent]
    );

    const { saveRatings } = useRatingSave({
        mode: "GROUPS",
        ratings,
        contentItems: contentItemsForSave,
        existingRatingsData: existingRatingsDataForSave,
        groupContext,
        selectedEpr: effectiveEprForContext,
        selectedTemplate,
        userDetails,
        token,
        setHasChanges: setHasRatingChanges,
        setRatingsLoaded,
        setExistingRatingsData: () => {}, // GROUPS re-fetches after save; no baseline to update
        fetchSelectedGroupInfo
    });

    const { saveComments } = useCommentSave({
        mode: "GROUPS",
        comments,
        fluencyByKey,
        existingComments,
        groupContext,
        userDetails,
        token,
        setHasCommentChanges,
        syncFromServer: syncComments
    });

    useAutoSave(saveRatings, hasRatingChanges);
    useAutoSave(saveComments, hasCommentChanges);

    // ── SLE ───────────────────────────────────────────────────────────────────
    const sleGroupInfoDto = useMemo(
        () =>
            buildSleGroupInfoDto({
                sleSelected,
                view: "GROUPS",
                selectedGroups,
                selectedEpr,
                selectedGrade,
                gradingProfiles,
                teacherGuid: overrideTeacherGuid || userDetails?.uid,
                profileLocationKey: schoolProfileLocationKey,
                effectiveTimePeriodDto
            }),
        [
            sleSelected,
            selectedGroups,
            selectedEpr,
            selectedGrade,
            gradingProfiles,
            overrideTeacherGuid,
            userDetails?.uid,
            schoolProfileLocationKey,
            effectiveTimePeriodDto
        ]
    );

    const groupSle = useGroupSleRatings({
        enabled: sleSelected,
        groupInfoDto: sleGroupInfoDto,
        schoolYearKey: schoolYearDto?.key,
        profileLocationKey: schoolProfileLocationKey,
        token,
        userGuid: userDetails?.uid
    });

    const groupSleStudents = useMemo(
        () => flattenGroupSleStudents(groupSle?.groupRows ?? [], sharedGradingProfileDtos),
        [groupSle?.groupRows, sharedGradingProfileDtos]
    );

    const groupSleState = useMemo(
        () => ({
            hasChanges: groupSle.hasChanges,
            loading: groupSle.loading,
            valuesByStudent: groupSle.valuesByStudent || {},
            sleCommentsByStudent: groupSle.sleCommentsByStudent || {},
            onChangeSleRating: groupSle.onChangeSleRating,
            onChangeSleComment: groupSle.onChangeSleComment,
            saveSleRatings: groupSle.saveGroupSleRatings,
            schoolLevelExpectations: groupSle.schoolLevelExpectations || [],
            groupRows: groupSle.groupRows || []
        }),
        [groupSle]
    );

    // ── Selection / routing effects ───────────────────────────────────────────
    // (Fetch / init effects live in useGroupRatingsLoader above)

    // Auto-set school location from router state
    useEffect(() => {
        if (locationState?.selectedSchoolLocationKey) {
            setSelectedSchoolLocationKey(Number(locationState.selectedSchoolLocationKey));
        }
    }, [locationState]);

    useEffect(() => {
        if (selectedSchoolLocationKey) return;
        if (locationState?.selectedSchoolLocationKey) return;
        if (sharedLocationKeys.length === 1) setSelectedSchoolLocationKey(sharedLocationKeys[0]);
        if (sharedLocationKeys.length === 0 && gradingProfilesCurrentSem.length > 0)
            setSelectedSchoolLocationKey(Number(schoolProfileLocationKey));
    }, [
        sharedLocationKeys,
        selectedSchoolLocationKey,
        schoolProfileLocationKey,
        locationState?.selectedSchoolLocationKey,
        gradingProfilesCurrentSem.length
    ]);

    // Auto-select single grade
    const onlyGrade = combinedGrades?.length === 1 ? combinedGrades[0] : null;
    const selectedEprKey = selectedEpr?.key == null ? "" : String(selectedEpr.key);

    useEffect(() => {
        if (!onlyGrade) return;
        if (locationState?.selectedGrade) return;
        if (selectedGrade !== onlyGrade) setSelectedGrade(onlyGrade);

        const defaultEpr = gradingProfilesCurrentSem?.find((p) => p.gradeName === onlyGrade) || null;
        const defaultKey = defaultEpr?.key == null ? "" : String(defaultEpr.key);
        if (defaultKey && defaultKey !== selectedEprKey) {
            dispatch({ type: "SET_SELECTED_EPR", payload: defaultEpr });
        } else if (!defaultKey && selectedEpr !== null) {
            dispatch({ type: "SET_SELECTED_EPR", payload: null });
        }
    }, [
        onlyGrade,
        selectedGrade,
        gradingProfilesCurrentSem,
        selectedEprKey,
        dispatch,
        selectedEpr,
        locationState?.selectedGrade
    ]);

    // Ensure school template for SLE override
    useEffect(() => {
        if (selectedSchoolLocationKey && token) {
            ensureTemplatesForLocation({ locationKey: selectedSchoolLocationKey });
        }
    }, [selectedSchoolLocationKey, token, ensureTemplatesForLocation]);

    // ── Rating / comment handlers ─────────────────────────────────────────────
    const handleRatingChange = useCallback(
        (studentKey, templateContentId, rating) => setRating(studentKey, templateContentId, rating),
        [setRating]
    );

    const handleBulkRating = useCallback(
        (templateContentId, rating) => {
            // Build a lookup from student object → groupId once, avoiding O(n²) nested finds
            const studentToGroupId = new Map();
            (selectedGroupContent || []).forEach((g) => {
                (g.groupStudentContents || []).forEach((s) => {
                    if (g.permission !== "VIEW") {
                        studentToGroupId.set(s, g.groupId);
                    }
                });
            });
            const editableKeys = [...studentToGroupId.entries()].map(
                ([s, groupId]) => `${groupId}-${s.gradingProfileStudentId}`
            );
            setBulkRating(templateContentId, rating, editableKeys);
        },
        [setBulkRating, selectedGroupContent]
    );

    const handleCommentChange = useCallback(
        (studentKey, comment, gradingCriteriaId) => setComment(`${studentKey}_${gradingCriteriaId}`, comment),
        [setComment]
    );

    const handleFluencyChange = useCallback(
        (studentKey, wpm, gradingCriteriaId) => setFluencyWpm(`${studentKey}_${gradingCriteriaId}`, wpm),
        [setFluencyWpm]
    );

    const handleSaveComment = useCallback(
        (context) => {
            if (!context) return;
            const { student, contentItem, rating, comment, key: existingKey } = context;
            const gradingCriteria = getGradingCriteriaId(
                student,
                selectedGroupContent,
                selectedEpr,
                selectedTemplate,
                "GROUPS"
            );
            const payload = {
                key: existingKey,
                gradingProfileStudentId: extractStudentGradingProfileId(student.key),
                gradingCriteria: Number(gradingCriteria),
                templateContentId: Number(contentItem.templateContentId),
                contentType: contentItem.contentType,
                rating: rating === "DOES NOT MEET" ? "DOES_NOT_MEET" : rating,
                comment: removeHiddenChars(comment.trim()),
                personalizedLearningPlan: false,
                lastRatedGuid: userDetails?.uid
            };
            ElementaryProgressReportDao({ action: "updateContentRating", ratingKey: existingKey, data: payload, token })
                .then((response) => {
                    if (response?.data?.payload) {
                        toast.success("Comment saved.");
                        fetchSelectedGroupInfo();
                        fetchSelectedGroupStudentComments();
                    }
                })
                .catch(() =>
                    toast.error("Comment was not saved. Please make note of your comment and try again later.")
                );
        },
        [
            selectedGroupContent,
            selectedEpr,
            selectedTemplate,
            userDetails,
            token,
            fetchSelectedGroupInfo,
            fetchSelectedGroupStudentComments
        ]
    );

    // Flush pending work BEFORE resetting on subject change. Await so the
    // update request finishes while this subject's state is still current.
    // skipRefresh avoids the GROUPS post-save refetch overwriting the next subject.
    const commitIfNeeded = useCallback(async () => {
        const pending = [];
        if (hasRatingChanges) pending.push(saveRatings({ skipRefresh: true }));
        if (hasCommentChanges) pending.push(saveComments());
        if (sleSelected && groupSle?.hasChanges) pending.push(groupSle.saveGroupSleRatings());
        if (pending.length) await Promise.all(pending);
    }, [hasRatingChanges, hasCommentChanges, saveRatings, saveComments, sleSelected, groupSle]);

    // Unmount-only leave flush (side nav / breadcrumb). Stable leaveKey so subject
    // switches stay on awaited commitIfNeeded above — do not double-flush via leaveKey.
    const leaveDirty = hasRatingChanges || hasCommentChanges || (sleSelected && !!groupSle?.hasChanges);
    useFlushOnLeave(commitIfNeeded, leaveDirty, "group-ratings");

    const handleSubjectChange = useCallback(
        async (e) => {
            const value = e.target.value;
            if (!value) return;
            await commitIfNeeded();
            handleSubjectSelect(value);
            if (value !== "SLE") {
                const templateId = Number(value);
                const subject = subjectOptions.find((s) => s.templateId === templateId) || null;
                setSelectedTemplate(subject);
            }
        },
        [commitIfNeeded, handleSubjectSelect, subjectOptions, setSelectedTemplate]
    );

    // Same flush-before-reset as subject change: deselecting a group remounts the
    // roster and rehydrates comments from the server. Without awaiting save first,
    // unsaved text (and in-flight autosave) is lost under slow network.
    const handleGroupsChange = useCallback(
        async (selected) => {
            await commitIfNeeded();
            setSelectedGroups(selected || []);
        },
        [commitIfNeeded, setSelectedGroups]
    );

    // ── Render ─────────────────────────────────────────────────────────────────
    const shouldShowGroupSelect = (selectedEpr || selectedGrade) && (selectedTemplate || sleSelected) && groupOptions;
    const shouldShowGroupSleTable = sleSelected && selectedGroups?.length > 0;
    let subjectDropdownValue = "";
    if (selectedTemplate) {
        subjectDropdownValue = String(selectedTemplate.templateId);
    } else if (sleSelected) {
        subjectDropdownValue = "SLE";
    }

    const customStyles = {
        container: (provided) => ({ ...provided, width: "68%", zIndex: 10 }),
        control: (base) => ({
            ...base,
            marginLeft: "0.5rem",
            fontSize: "1.15rem",
            fontWeight: "500",
            border: "2px solid #0A4064",
            borderRadius: "8px",
            backgroundColor: "white",
            color: "#0A4064"
        }),
        groupHeading: (base) => ({ ...base, fontWeight: "bold", fontSize: "0.9rem", color: "#333" })
    };

    return (
        <div className="right-side-wrapper" style={navCollapsed ? { maxWidth: "95%" } : { maxWidth: "80%" }}>
            <div className="right-page-title">
                <h4>
                    Input Ratings <span>For Group(s)</span>
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto?.name}
                </h5>
            </div>
            <hr />

            {!gradingProfilesCurrentSem?.length && !allSharedGroupOptions.length && (
                <div>
                    <b>Homeroom Teachers:</b> you must first create your homeroom Report Card <b>OR</b> your
                    colleague(s) must share a group with you before you can input ratings.
                    <br />
                    <br />
                    <b>Specials/Elective Teachers:</b> each homeroom teacher will share their homeroom class with you to
                    allow you to input ratings for the subject you teach. Currently, no homeroom teacher has shared a
                    class with you.
                </div>
            )}

            <div style={{ display: "flex", gap: "0px 24px", flexWrap: "wrap" }}>
                {showSchoolSelect && (
                    <section className="epr-selector-ratings">
                        <h5>Select a School:</h5>
                        <select
                            className="rating-select-dropdown"
                            value={selectedSchoolLocationKey || ""}
                            onChange={(e) => {
                                setSelectedSchoolLocationKey(Number(e.target.value));
                                setSelectedGrade(null);
                                resetForGroupGradeChange();
                            }}
                        >
                            <option value="" disabled>
                                Select School
                            </option>
                            {sharedLocationKeys.map((k) => (
                                <option key={k} value={k}>
                                    {allLocationDtos?.find((loc) => Number(loc.key) === Number(k))?.name ||
                                        `School ${k}`}
                                </option>
                            ))}
                        </select>
                    </section>
                )}

                {Boolean(shouldShowGroupEprSelect) && (
                    <section className="epr-selector-ratings">
                        <h5>Select a Grade:</h5>
                        <label className="visually-hidden" htmlFor="groupGradeSelect">
                            Select a Grade
                        </label>
                        <select
                            className="rating-select-dropdown"
                            id="groupGradeSelect"
                            value={selectedGrade || ""}
                            onChange={handleGroupGradeChange}
                        >
                            <option value="" disabled>
                                Select Grade
                            </option>
                            {gradesForGroupSelect.map((gradeName) => (
                                <option key={gradeName} value={gradeName}>
                                    {gradeName === "K" ? "Kindergarten" : `${gradeName} Grade`}
                                </option>
                            ))}
                        </select>
                    </section>
                )}

                {Boolean(selectedEpr || (selectedGrade && allSharedGroupOptions.length > 0)) && (
                    <div className="subject-dropdown-wrapper">
                        <section className="epr-selector-ratings">
                            <h5>Select a Subject:</h5>
                            <label className="visually-hidden" htmlFor="groupSubjectDropdown">
                                Select a Subject
                            </label>
                            <select
                                id="groupSubjectDropdown"
                                className="rating-select-dropdown"
                                onChange={handleSubjectChange}
                                value={subjectDropdownValue}
                            >
                                <option value="" disabled>
                                    Select a Subject
                                </option>
                                {hasSleSubjectOption && <option value="SLE">SLE</option>}
                                {subjectOptions.map((s) => (
                                    <option key={s.templateId} value={s.templateId}>
                                        {s.subjectName}
                                    </option>
                                ))}
                            </select>
                        </section>
                    </div>
                )}
            </div>

            {shouldShowGroupSelect && (
                <section className="group-select-section">
                    <h5>Select Group(s):</h5>
                    <Select
                        isMulti
                        noOptionsMessage={() =>
                            selectedGroups?.length > 0
                                ? "No additional groups available for this subject."
                                : "No groups available for this subject."
                        }
                        options={groupOptions}
                        value={selectedGroups}
                        onChange={handleGroupsChange}
                        placeholder="Select groups..."
                        styles={customStyles}
                    />
                </section>
            )}

            {shouldShowGroupSleTable && (
                <SleRatingTable
                    actionWindowsDto={actionWindowsDto}
                    mode="GROUPS"
                    teachersProfileStatus={
                        selectedEpr?.status ??
                        gradingProfilesCurrentSem?.find((p) => p.gradeName === selectedGrade)?.status
                    }
                    sharedGradingProfileDtos={sharedGradingProfileDtos}
                    sleState={groupSleState}
                    students={groupSleStudents}
                    token={token}
                    schoolYearKey={schoolYearDto?.key}
                    basePath={basePath}
                    userDetails={userDetails}
                />
            )}

            {selectedGroupContent?.length > 0 && (
                <RatingsContentSwitch
                    key={selectedTemplate?.templateId}
                    diffProps={{
                        actionWindowsDto,
                        basePath,
                        groupData: selectedGroupContent,
                        mode: "GROUPS",
                        onSaveSubjectComments: saveComments,
                        onSubjectCommentChange: handleCommentChange,
                        selectedEpr: effectiveEprForContext,
                        selectedSchoolLocationKey,
                        selectedTemplate,
                        subjectComments: comments,
                        token,
                        userDetails
                    }}
                    ratingGridProps={{
                        actionWindowsDto,
                        basePath,
                        comments,
                        fluencyByKey,
                        existingRatingsData: existingRatingsDataForSave,
                        groupData: selectedGroupContent,
                        hasChanges,
                        hasCommentChanges,
                        mode: "GROUPS",
                        onBulkRating: handleBulkRating,
                        onCommentChange: handleCommentChange,
                        onFluencyChange: handleFluencyChange,
                        onRatingChange: handleRatingChange,
                        onSave: saveRatings,
                        onSaveComments: saveComments,
                        onSaveComment: handleSaveComment,
                        ratings,
                        ratingsLoaded,
                        selectedEpr,
                        fluencyGradeName: selectedGrade,
                        selectedSchoolLocationKey,
                        selectedTemplate,
                        token,
                        userDetails
                    }}
                />
            )}
        </div>
    );
};

GroupRatings.propTypes = {
    basePath: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    state: PropTypes.shape({
        gradingProfiles: PropTypes.array,
        navCollapsed: PropTypes.bool,
        selectedEpr: PropTypes.object
    }).isRequired,
    teacherGuid: PropTypes.string
};

export default GroupRatings;
